import React, { createContext, useContext, useState, useEffect } from 'react';
import { db, discordConfig } from '../config/firebase';
import {
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  serverTimestamp,
  collection,
  getDocs,
  updateDoc,
  query,
  where
} from 'firebase/firestore';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

// Extract normalized name from Discord serverNickname
// Discord nicknames are formatted as "Rank F. LastName" (e.g., "2nd Lt J. Smith", "MSgt A. Jones")
// We extract "F. LastName" pattern by finding the period and getting the letter before + word after
const extractNormalizedName = (fullName) => {
  const name = fullName.trim();

  // Find the period in the name (e.g., "J." in "2nd Lt J. Smith")
  const periodIndex = name.indexOf('.');

  if (periodIndex > 0 && periodIndex < name.length - 1) {
    // Get the character before the period (first initial)
    const firstInitial = name[periodIndex - 1];

    // Get everything after the period, trimmed (last name)
    const lastName = name.substring(periodIndex + 1).trim();

    if (firstInitial && lastName) {
      // Return format: "J. Smith"
      return `${firstInitial}. ${lastName}`;
    }
  }

  // Fallback: return the original name if pattern doesn't match
  return name;
};

// Session storage key
const SESSION_KEY = 'discord_session';
const SESSION_DURATION = 48 * 60 * 60 * 1000; // 48 hours in milliseconds
const ROLE_CHECK_COOLDOWN = 60000; // Minimum 60 seconds between role checks

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [discordUser, setDiscordUser] = useState(null);
  const [lastRoleCheck, setLastRoleCheck] = useState(0);
  const [roleCheckInProgress, setRoleCheckInProgress] = useState(false);

  // Check if user has the required role for site access
  const hasRequiredRole = (memberRoles) => {
    const { requiredRoleId } = discordConfig;
    if (!requiredRoleId) return true; // No required role configured
    return memberRoles.includes(requiredRoleId);
  };

  // Map Discord roles to app roles
  const mapDiscordRolesToAppRole = (memberRoles) => {
    const { roleMapping } = discordConfig;

    // Check roles in priority order (admin > armorer > user)
    for (const [discordRoleId, appRole] of Object.entries(roleMapping)) {
      if (memberRoles.includes(discordRoleId)) {
        if (appRole === 'admin') return 'admin';
        if (appRole === 'armorer') return 'armorer';
        if (appRole === 'user') return 'user';
      }
    }
    return null; // No valid role found
  };

  // Initiate Discord OAuth flow
  const signInWithDiscord = () => {
    const { clientId, redirectUri, scope, authUrl } = discordConfig;

    const params = new URLSearchParams({
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: scope,
      prompt: 'consent', // Force re-authorization to get a fresh code
    });

    window.location.href = `${authUrl}?${params.toString()}`;
  };

  // Handle Discord OAuth callback
  const handleDiscordCallback = async (code) => {
    try {
      const { clientId, clientSecret, redirectUri, tokenUrl, apiUrl, guildId } = discordConfig;

      // Exchange code for access token
      const tokenResponse = await fetch(tokenUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          client_id: clientId,
          client_secret: clientSecret,
          grant_type: 'authorization_code',
          code: code,
          redirect_uri: redirectUri,
        }),
      });

      if (!tokenResponse.ok) {
        const errorData = await tokenResponse.json();
        throw new Error(errorData.error_description || 'Failed to exchange code for token');
      }

      const tokenData = await tokenResponse.json();
      const accessToken = tokenData.access_token;

      // Fetch user info from Discord
      const userResponse = await fetch(apiUrl, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!userResponse.ok) {
        throw new Error('Failed to fetch Discord user info');
      }

      const discordUserData = await userResponse.json();

      // Fetch user's guild membership and roles
      const guildMemberResponse = await fetch(
        `https://discord.com/api/users/@me/guilds/${guildId}/member`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!guildMemberResponse.ok) {
        if (guildMemberResponse.status === 404) {
          return {
            success: false,
            message: 'You must be a member of the Discord server to access this application.'
          };
        }
        throw new Error('Failed to verify Discord server membership');
      }

      const memberData = await guildMemberResponse.json();
      const memberRoles = memberData.roles || [];
      // Server nickname (falls back to username if no nickname set)
      // Trim everything after the first "|" (e.g., "J. Smith | Alpha Team" -> "J. Smith")
      const rawNickname = memberData.nick || discordUserData.username;
      const serverNickname = rawNickname.split('|')[0].trim();

      // Check if user has required role for site access
      if (!hasRequiredRole(memberRoles)) {
        return {
          success: false,
          message: 'You must be a badged member to access this application. Please contact an admin if you believe this is an error.'
        };
      }

      // Map Discord roles to app role
      const appRole = mapDiscordRolesToAppRole(memberRoles);

      // If no valid role found (shouldn't happen if hasRequiredRole passed)
      if (!appRole) {
        return {
          success: false,
          message: 'You do not have the required role to access this application.'
        };
      }

      // Create/update user in Firestore with migration logic for existing users
      // First, check if a document with this Discord ID already exists
      const discordUserRef = doc(db, 'users', discordUserData.id);
      const discordUserSnap = await getDoc(discordUserRef);

      let userId = discordUserData.id; // Default to Discord ID
      let isExistingUser = false;

      if (discordUserSnap.exists()) {
        // User already logged in with Discord before - just update
        await updateDoc(discordUserRef, {
          lastLogin: serverTimestamp(),
          discordUsername: discordUserData.username,
          serverNickname: serverNickname,
        });
        isExistingUser = true;
      } else {
        // First Discord login - try to find existing user by username match
        const normalizedName = extractNormalizedName(serverNickname);

        // Search for existing user by username (old Firebase Auth users)
        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('username', '==', normalizedName));
        const snapshot = await getDocs(q);

        if (snapshot.docs.length > 0) {
          // Found existing user - augment with Discord info
          const existingUserDoc = snapshot.docs[0];
          userId = existingUserDoc.id; // Keep the old Firebase UID

          await updateDoc(existingUserDoc.ref, {
            discordId: discordUserData.id,
            discordUsername: discordUserData.username,
            serverNickname: serverNickname,
            lastLogin: serverTimestamp(),
          });

          isExistingUser = true;
          console.log(`Migrated existing user ${normalizedName} (${userId}) with Discord ID ${discordUserData.id}`);
        } else {
          // No existing user found - create new document keyed by Discord ID
          const userDocData = {
            discordId: discordUserData.id,
            discordUsername: discordUserData.username,
            serverNickname: serverNickname,
            username: normalizedName, // Store normalized name for consistency
            email: discordUserData.email || '',
            createdAt: serverTimestamp(),
            lastLogin: serverTimestamp(),
          };

          await setDoc(discordUserRef, userDocData);
          console.log(`Created new user with Discord ID ${discordUserData.id}`);
        }
      }

      // Extract the normalized name (e.g., "J. Draco" from "T Sgt. J. Draco")
      const normalizedName = extractNormalizedName(serverNickname);

      // Store session in localStorage
      // userId may be the old Firebase UID (for migrated users) or Discord ID (for new users)
      const session = {
        discordAccessToken: accessToken,
        discordTokenExpiry: Date.now() + SESSION_DURATION,
        discordUser: {
          id: discordUserData.id,
          username: discordUserData.username,
          serverNickname: serverNickname,
          normalizedName: normalizedName,
          email: discordUserData.email || '',
        },
        userId: userId, // The Firestore document ID (may differ from Discord ID for migrated users)
        userRole: appRole,
      };
      localStorage.setItem(SESSION_KEY, JSON.stringify(session));

      // Update React state
      // Use userId for Firestore operations, discordUser.id for Discord API calls
      setUser({ uid: userId });
      setDiscordUser(session.discordUser);
      setUserRole(appRole);
      setUserData({
        username: normalizedName, // Use normalized name as primary username
        serverNickname: serverNickname,
        normalizedName: normalizedName,
        discordUsername: discordUserData.username,
        discordId: discordUserData.id,
        firestoreId: userId, // The actual Firestore document ID
        email: discordUserData.email,
      });

      return { success: true };
    } catch (error) {
      console.error('Discord callback error:', error);
      return { success: false, message: error.message };
    }
  };

  // Sign out
  const logout = () => {
    localStorage.removeItem(SESSION_KEY);
    setUser(null);
    setUserRole(null);
    setUserData(null);
    setDiscordUser(null);
  };

  // Refresh roles from Discord (called on route changes)
  const refreshRoles = async () => {
    // Prevent multiple simultaneous checks
    if (roleCheckInProgress) return { success: true, changed: false };

    // Cooldown to prevent excessive API calls
    const now = Date.now();
    if (now - lastRoleCheck < ROLE_CHECK_COOLDOWN) {
      return { success: true, changed: false };
    }

    // Get stored session
    const stored = localStorage.getItem(SESSION_KEY);
    if (!stored) return { success: false, reason: 'no_session' };

    const session = JSON.parse(stored);
    if (!session.discordAccessToken) return { success: false, reason: 'no_token' };

    // Check if token is expired
    if (session.discordTokenExpiry <= Date.now()) {
      logout();
      return { success: false, reason: 'token_expired' };
    }

    setRoleCheckInProgress(true);
    setLastRoleCheck(now);

    try {
      const { guildId } = discordConfig;

      // Fetch current guild membership and roles
      const guildMemberResponse = await fetch(
        `https://discord.com/api/users/@me/guilds/${guildId}/member`,
        {
          headers: {
            Authorization: `Bearer ${session.discordAccessToken}`,
          },
        }
      );

      if (!guildMemberResponse.ok) {
        const status = guildMemberResponse.status;

        if (status === 401) {
          // Token expired or revoked - force re-login
          console.log('Discord token expired, requiring re-login');
          logout();
          return { success: false, reason: 'token_invalid' };
        }

        if (status === 404 || status === 403) {
          // User is no longer in the server - force logout
          console.log('User no longer in Discord server, logging out');
          logout();
          return { success: false, reason: 'not_in_server' };
        }

        // Other errors (rate limit, server error) - don't log out, just skip this check
        console.warn('Discord API error during role check:', status);
        return { success: true, changed: false, skipped: true };
      }

      const memberData = await guildMemberResponse.json();
      const memberRoles = memberData.roles || [];

      // Check if user still has required role
      if (!hasRequiredRole(memberRoles)) {
        console.log('User no longer has required badge role, logging out');
        logout();
        return { success: false, reason: 'missing_required_role' };
      }

      // Get updated nickname
      const rawNickname = memberData.nick || session.discordUser.username;
      const serverNickname = rawNickname.split('|')[0].trim();

      // Map to app role
      const newRole = mapDiscordRolesToAppRole(memberRoles);

      // If role mapping returns null (no valid role), log out
      if (!newRole) {
        console.log('User no longer has a valid role, logging out');
        logout();
        return { success: false, reason: 'no_valid_role' };
      }

      const oldRole = session.userRole;
      const roleChanged = newRole !== oldRole;
      const nicknameChanged = serverNickname !== session.discordUser.serverNickname;

      if (roleChanged || nicknameChanged) {
        // Update session in localStorage
        const updatedSession = {
          ...session,
          userRole: newRole,
          discordUser: {
            ...session.discordUser,
            serverNickname: serverNickname,
          },
        };
        localStorage.setItem(SESSION_KEY, JSON.stringify(updatedSession));

        // Update React state
        setUserRole(newRole);
        setDiscordUser(updatedSession.discordUser);
        setUserData(prev => ({
          ...prev,
          serverNickname: serverNickname,
        }));

        console.log(`Role/nickname updated: ${oldRole} -> ${newRole}, nickname: ${serverNickname}`);
      }

      return { success: true, changed: roleChanged, newRole, oldRole };
    } catch (error) {
      console.error('Error refreshing Discord roles:', error);
      return { success: false, reason: 'error', error };
    } finally {
      setRoleCheckInProgress(false);
    }
  };

  // Admin: Get all users
  const getAllUsers = async () => {
    try {
      const usersRef = collection(db, 'users');
      const snapshot = await getDocs(usersRef);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting users:', error);
      return [];
    }
  };

  // Admin: Update user role (Note: This is for display only - Discord roles are source of truth)
  const updateUserRole = async (userId, newRole) => {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, { displayRole: newRole });
      return { success: true };
    } catch (error) {
      console.error('Error updating role:', error);
      return { success: false, message: error.message };
    }
  };

  // Admin: Approve user
  const approveUser = async (userId) => {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, { approved: true, approvedAt: serverTimestamp() });
      return { success: true };
    } catch (error) {
      console.error('Error approving user:', error);
      return { success: false, message: error.message };
    }
  };

  // Admin: Reject (delete) user
  const rejectUser = async (userId) => {
    try {
      const userRef = doc(db, 'users', userId);
      await deleteDoc(userRef);
      return { success: true };
    } catch (error) {
      console.error('Error rejecting user:', error);
      return { success: false, message: error.message };
    }
  };

  // Get all armorers (for assignment dropdown)
  const getArmorers = async () => {
    try {
      // Since roles are now in Discord, we fetch users who have logged in
      // and filter by their last known role or check Discord
      const usersRef = collection(db, 'users');
      const snapshot = await getDocs(usersRef);
      // For now, return all users - in practice you'd want to cache roles
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting armorers:', error);
      return [];
    }
  };

  // Restore session on mount
  useEffect(() => {
    const restoreSession = () => {
      try {
        const stored = localStorage.getItem(SESSION_KEY);
        if (stored) {
          const session = JSON.parse(stored);

          if (session.discordTokenExpiry > Date.now()) {
            // Valid session - restore from localStorage
            // Use userId if available (for migrated users), otherwise fall back to Discord ID
            const firestoreId = session.userId || session.discordUser.id;
            // Get normalized name, or extract it if not present (for older sessions)
            const normalizedName = session.discordUser.normalizedName || 
              extractNormalizedName(session.discordUser.serverNickname || session.discordUser.username);
            
            setUser({ uid: firestoreId });
            setDiscordUser(session.discordUser);
            setUserRole(session.userRole);
            setUserData({
              username: normalizedName, // Use normalized name as primary
              serverNickname: session.discordUser.serverNickname,
              normalizedName: normalizedName,
              discordUsername: session.discordUser.username,
              discordId: session.discordUser.id,
              firestoreId: firestoreId,
              email: session.discordUser.email,
            });
          } else {
            // Expired - clear session
            localStorage.removeItem(SESSION_KEY);
          }
        }
      } catch (error) {
        console.error('Error restoring session:', error);
        localStorage.removeItem(SESSION_KEY);
      }
      setLoading(false);
    };

    restoreSession();
  }, []);

  const value = {
    user,
    userRole,
    userData,
    discordUser,
    loading,
    signInWithDiscord,
    handleDiscordCallback,
    logout,
    refreshRoles,
    getAllUsers,
    updateUserRole,
    approveUser,
    rejectUser,
    getArmorers,
    isAdmin: userRole === 'admin',
    isArmorer: userRole === 'armorer' || userRole === 'admin',
    isUser: userRole === 'user',
    isPending: false, // No pending state with Discord - server membership = approval
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
